import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';
import { AuthService } from '@app/core/services/auth.service';
import { RoleService } from '@app/shared/services/roles.service';

@Component({
    selector: 'app-login',
    imports: [CommonModule, UiLibraryModule],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss'
})
export class LoginComponent {
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly roleService = inject(RoleService);
  private readonly queryParamMap = toSignal(this.route.queryParamMap, {
    initialValue: this.route.snapshot.queryParamMap,
  });

  readonly isSubmitting = signal(false);
  readonly submissionFailure = signal<string | null>(null);
  readonly loginForm = new UntypedFormGroup({});
  readonly loginFormConfig: InputConfig[] = [
    {
      name: 'username',
      type: EzFormInputType.TEXT,
      label: 'Username',
      row: 1,
      width: 'fill',
      validation: [Validators.required],
      validationMessages: {
        required: 'Username is required.',
      },
    },
    {
      name: 'password',
      type: EzFormInputType.PASSWORD,
      label: 'Password',
      row: 2,
      width: 'fill',
      validation: [Validators.required],
      validationMessages: {
        required: 'Password is required.',
      },
    },
  ];
  readonly returnUrl = computed(() =>
    this.authService.normalizeReturnUrl(this.queryParamMap().get('returnUrl')),
  );
  readonly routeFailure = computed(() => this.queryParamMap().get('authFailure'));
  readonly isAssuranceChallenge = computed(() => ['auth.assurance_required', 'auth.assurance_expired'].includes(this.routeFailure() ?? ''));

  readonly config = computed(() => this.authService.loginPageConfig);
  readonly loginAvailable = computed(() => this.authService.isLoginAvailable());
  readonly isInternalPasswordProvider = computed(() => this.authService.isInternalPasswordDefaultProvider());
  readonly helpLinkText = computed(() => this.config().helpLinkText || 'Need help signing in?');
  readonly brandMark = computed(() => {
    const title = this.config().applicationName || 'RJ';
    return title
      .split(/\s+/)
      .filter((segment) => segment.length > 0)
      .slice(0, 2)
      .map((segment) => segment[0]?.toUpperCase() ?? '')
      .join('');
  });
  readonly failureMessage = computed(() => {
    const failure = this.submissionFailure() ?? this.routeFailure();
    if (!failure) {
      return '';
    }

    return this.toUserSafeFailureMessage(failure);
  });

  constructor() {
    if (this.roleService.accessProfileState() === 'loaded' && !this.isAssuranceChallenge()) {
      void this.router.navigateByUrl(this.returnUrl(), {
        replaceUrl: true,
      });
    }
  }

  async submit(): Promise<void> {
    if (this.isInternalPasswordProvider()) {
      await this.submitInternalPasswordLogin();
      return;
    }

    this.startProviderLogin();
  }

  onInternalFormEnter(event: Event): void {
    if (!this.isInternalPasswordProvider()) {
      return;
    }

    event.preventDefault();
    void this.submit();
  }

  private startProviderLogin(): void {
    if (!this.loginAvailable() || this.isSubmitting()) {
      return;
    }

    this.submissionFailure.set(null);
    this.isSubmitting.set(true);
    this.authService.startLogin(this.returnUrl());
  }

  private async submitInternalPasswordLogin(): Promise<void> {
    if (!this.loginAvailable() || this.isSubmitting()) {
      return;
    }

    this.submissionFailure.set(null);
    const usernameControl = this.loginForm.get('username');
    const passwordControl = this.loginForm.get('password');
    if (!usernameControl || !passwordControl) {
      return;
    }

    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const failureCode = await this.authService.loginWithInternalPassword(
      {
        username: `${usernameControl.value ?? ''}`.trim(),
        password: `${passwordControl.value ?? ''}`,
      },
      this.returnUrl(),
    );

    if (failureCode) {
      this.submissionFailure.set(failureCode);
      passwordControl.reset('');
    }

    this.isSubmitting.set(false);
  }

  private toUserSafeFailureMessage(failureCode: string): string {
    switch (failureCode) {
      case 'auth.assurance_required':
      case 'auth.assurance_expired':
        return 'Sign in again to confirm your identity before continuing this action.';
      case 'credentials_rejected':
        return 'The supplied credentials could not be authenticated.';
      case 'login_unavailable':
        return 'Sign-in is not available in the current environment configuration.';
      case 'login_failed':
        return 'Sign-in could not be completed with the current browser session.';
      case 'remote_failure':
        return 'The provider sign-in did not complete. Try again or contact support if the problem persists.';
      case 'missing_actor':
      case 'unmapped_actor':
      case 'missing_principal':
        return 'Your account could not be mapped to an approved platform user.';
      case 'no_effective_access':
        return 'Your account signed in, but it does not currently have access to this application.';
      default:
        return 'Sign-in could not be completed with the current browser session.';
    }
  }
}
